// src/supplier/pages/SupplierLogin.jsx
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useSupplierAuth } from "../context/SupplierAuthContext";
import { supplierAuthAPI } from "../services/supplierApi";
import { Building2, Loader2 } from "lucide-react";
import PasswordInput from "../../components/PasswordInput";
import ThemeToggle from "../../components/ThemeToggle";
import Swal from "../../utils/apparifySwal";
import { getLoginErrorDialogOptions } from "../../utils/loginError";

export default function SupplierLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useSupplierAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    document.body.classList.add("supplier-portal-active");
    return () => document.body.classList.remove("supplier-portal-active");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    Swal.fire({
      title: "Signing in...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const res = await supplierAuthAPI.login(form);
      if (!res?.token) throw new Error("Login failed — no token returned");
      // Fetch full profile with supplier links
      const meRes = await (async () => {
        // Temporarily store token so getMe can use it
        localStorage.setItem("supplier_session", JSON.stringify({ token: res.token, user: res.user }));
        return supplierAuthAPI.getMe();
      })();
      await login({
        token: res.token,
        user: meRes?.data || res.user,
        suppliers: meRes?.data?.suppliers || [],
      });
      await Swal.fire({
        icon: "success",
        title: "Welcome!",
        html: "You are now signed in.",
        timer: 1200,
        showConfirmButton: false,
      });
      navigate("/supplier");
    } catch (err) {
      Swal.close();
      const payload = err?.response?.data || {};
      // If email verification is required, redirect to verify page
      if (payload?.code === "EMAIL_VERIFICATION_REQUIRED") {
        const verification = payload?.errors || {};
        const email = verification.email || form.email;
        try { sessionStorage.setItem("supplier_pending_verification_email", email.trim().toLowerCase()); } catch {}
        navigate("/supplier/verify-email", {
          state: { email, verification, fromLogin: true },
        });
        return;
      }
      localStorage.removeItem("supplier_session");
      await Swal.fire(getLoginErrorDialogOptions(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="supplier-portal relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f5f5f4] px-4 py-16 dark:bg-[#0a0a0a]">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-70 dark:hidden"
          style={{
            backgroundImage:
              "linear-gradient(rgba(23, 23, 23, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(23, 23, 23, 0.045) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div
          className="absolute inset-0 hidden opacity-80 dark:block"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="absolute left-1/2 top-1/2 h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80 blur-3xl dark:bg-white/[0.06] sm:h-[36rem] sm:w-[36rem]" />

        <svg
          className="absolute -left-12 top-10 h-72 w-80 text-neutral-900/10 dark:text-white/10 sm:left-8 sm:h-96 sm:w-[28rem]"
          viewBox="0 0 440 380"
          fill="none"
        >
          <path d="M42 88L154 42L260 126L392 72" stroke="currentColor" strokeWidth="1.5" />
          <path d="M154 42L126 224L284 300L392 212" stroke="currentColor" strokeWidth="1.5" />
          <path d="M260 126L284 300" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="42" cy="88" r="7" fill="currentColor" />
          <circle cx="154" cy="42" r="5" fill="currentColor" />
          <circle cx="260" cy="126" r="8" fill="currentColor" />
          <circle cx="392" cy="72" r="5" fill="currentColor" />
          <circle cx="126" cy="224" r="6" fill="currentColor" />
          <circle cx="284" cy="300" r="7" fill="currentColor" />
          <circle cx="392" cy="212" r="5" fill="currentColor" />
        </svg>

        <svg
          className="absolute -bottom-12 -right-20 h-72 w-80 rotate-180 text-neutral-900/[0.07] dark:text-white/[0.07] sm:right-10 sm:h-96 sm:w-[28rem]"
          viewBox="0 0 440 380"
          fill="none"
        >
          <path d="M42 88L154 42L260 126L392 72" stroke="currentColor" strokeWidth="1.5" />
          <path d="M154 42L126 224L284 300L392 212" stroke="currentColor" strokeWidth="1.5" />
          <path d="M260 126L284 300" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="42" cy="88" r="7" fill="currentColor" />
          <circle cx="154" cy="42" r="5" fill="currentColor" />
          <circle cx="260" cy="126" r="8" fill="currentColor" />
          <circle cx="392" cy="72" r="5" fill="currentColor" />
          <circle cx="126" cy="224" r="6" fill="currentColor" />
          <circle cx="284" cy="300" r="7" fill="currentColor" />
          <circle cx="392" cy="212" r="5" fill="currentColor" />
        </svg>

        <div className="absolute bottom-8 left-8 hidden items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-neutral-900/35 dark:text-white/30 sm:flex">
          <span className="h-px w-10 bg-current" />
          Apparify Supply
        </div>
      </div>

      <ThemeToggle
        variant="icon"
        className="fixed bottom-5 right-5 z-50 sm:bottom-6 sm:right-6"
      />

      <div className="relative z-10 w-full max-w-[488px] rounded-2xl border border-white/80 bg-white/90 p-6 shadow-[0_24px_70px_-28px_rgba(0,0,0,0.35)] backdrop-blur-xl dark:border-white/10 dark:bg-neutral-900/85 dark:shadow-[0_24px_70px_-28px_rgba(0,0,0,0.9)]">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/50">
            <Building2 className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Supplier Portal</h1>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Sign in to manage your RFQs, orders, and deliveries</p>
        </div>

        {params.get("registered") && (
          <div className="mb-4 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
            Registration successful! Please sign in.
          </div>
        )}

        {params.get("verified") && (
          <div className="mb-4 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
            Email verified! You can now sign in.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Email</label>
            <input
              type="email" required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
              placeholder="supplier@example.com"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Password</label>
            <PasswordInput
              required
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
          Don't have an account?{" "}
          <Link to="/supplier/register" className="text-blue-600 hover:underline dark:text-blue-400">
            Register here
          </Link>
        </p>
      </div>
    </div>
  );
}
